/**
 * Case Studies Tabs — frontend behaviour.
 *
 * Builds an accessible WAI-ARIA tabs widget from the server-rendered panels.
 * All case study content is already present in the markup (crawlable and
 * accessible without JS); this script only adds the tablist and the
 * show/hide + keyboard interaction on top of it.
 */
document.querySelectorAll( '.b-case-studies-tabs' ).forEach( ( block, blockIndex ) => {

	const panelsWrap = block.querySelector( '.b-case-studies-tabs__panels' );
	const panels = panelsWrap ? Array.from( panelsWrap.querySelectorAll( ':scope > .b-case-study-tab' ) ) : [];

	if ( ! panels.length ) {

		return;

	}

	const idPrefix = `case-studies-tabs-${ blockIndex }`;

	/**
	 * Build the tablist button bar and insert it before the panels.
	 *
	 * @return {HTMLElement[]} The created tab button elements.
	 */
	const buildTablist = () => {

		const tablist = document.createElement( 'div' );
		tablist.className = 'b-case-studies-tabs__tablist';
		tablist.setAttribute( 'role', 'tablist' );

		const tablistLabel = panelsWrap.dataset.tablistLabel;
		if ( tablistLabel ) {

			tablist.setAttribute( 'aria-label', tablistLabel );

		}

		const tabs = panels.map( ( panel, index ) => {

			const labelEl = panel.querySelector( '.b-case-study-tab__tab-label' );
			const label = labelEl ? labelEl.textContent.trim() : '';

			const tabId = `${ idPrefix }-tab-${ index }`;
			const panelId = `${ idPrefix }-panel-${ index }`;
			panel.id = panelId;
			panel.setAttribute( 'role', 'tabpanel' );
			panel.setAttribute( 'aria-labelledby', tabId );

			const tab = document.createElement( 'button' );
			tab.type = 'button';
			tab.className = 'b-case-studies-tabs__tab';
			tab.id = tabId;
			tab.setAttribute( 'role', 'tab' );
			tab.setAttribute( 'aria-controls', panelId );
			tab.textContent = label;
			tablist.appendChild( tab );

			return tab;

		} );

		panels[ 0 ].parentNode.insertBefore( tablist, panels[ 0 ] );

		return tabs;

	};

	/**
	 * Activate a tab/panel pair by index and move focus to the tab.
	 *
	 * @param {HTMLElement[]} tabs       Tab button elements.
	 * @param {number}        activeIndex Index to activate.
	 * @param {boolean}       focusTab    Whether to move focus to the tab.
	 */
	const setActiveTab = ( tabs, activeIndex, focusTab ) => {

		tabs.forEach( ( tab, index ) => {

			const isActive = index === activeIndex;
			tab.setAttribute( 'aria-selected', String( isActive ) );
			tab.tabIndex = isActive ? 0 : -1;
			panels[ index ].hidden = ! isActive;

			const dot = tab.querySelector( '.b-case-studies-tabs__tab-dot' );

			if ( isActive && ! dot ) {

				const newDot = document.createElement( 'span' );
				newDot.className = 'b-case-studies-tabs__tab-dot';
				tab.prepend( newDot );

			} else if ( ! isActive && dot ) {

				dot.remove();

			}

			tab.classList.toggle( 'is-active', isActive );

		} );

		if ( focusTab ) {

			tabs[ activeIndex ].focus();

		}

	};

	/**
	 * Handle arrow-key / home / end navigation between tabs.
	 *
	 * @param {HTMLElement[]} tabs Tab button elements.
	 * @param {KeyboardEvent} event Keydown event.
	 * @param {number} currentIndex Currently focused tab index.
	 */
	const handleKeydown = ( tabs, event, currentIndex ) => {

		const keys = {
			ArrowRight: () => ( currentIndex + 1 ) % tabs.length,
			ArrowLeft: () => ( currentIndex - 1 + tabs.length ) % tabs.length,
			Home: () => 0,
			End: () => tabs.length - 1,
		};

		if ( ! keys[ event.key ] ) {

			return;

		}

		event.preventDefault();
		setActiveTab( tabs, keys[ event.key ](), true );

	};

	const tabs = buildTablist();

	tabs.forEach( ( tab, index ) => {

		tab.addEventListener( 'click', () => setActiveTab( tabs, index, false ) );
		tab.addEventListener( 'keydown', ( event ) => handleKeydown( tabs, event, index ) );

	} );

	setActiveTab( tabs, 0, false );

} );
